import { ButtonIcon, Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import BigNumber from "bignumber.js";
import { useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { TbDeviceFloppy, TbPlus, TbTrash } from "react-icons/tb";
import {
  ManageOfferBodySchema,
  type PurchaseFee,
  type PurchaseOffer,
  toBigNumber,
} from "shared";
import { Api } from "@/api.config";
import { Button } from "@/components/Button";
import { FeeListFields } from "@/components/form/FeeListFields";
import { FormGroup } from "@/components/form/FormGroup";
import { NumberInput } from "@/components/form/NumberInput";
import { PriceInput, Transformers } from "@/components/form/PriceInput";
import { TextField } from "@/components/form/TextField";
import { QueryKeys } from "@/LinkGen";
import styles from "@/offers/ManageOfferDialog.module.css";

function getOfferDefault(offers: PurchaseOffer[] | undefined) {
  if (!offers || offers.length === 0) {
    return [
      {
        ID: null,
        name: "Long",
        investor: "",
        interest_rate: "",
        discount_points: "",
      },
      {
        ID: null,
        name: "Medium",
        investor: "",
        interest_rate: "",
        discount_points: "",
      },
      {
        ID: null,
        name: "Short",
        investor: "",
        interest_rate: "",
        discount_points: "",
      },
    ];
  }

  return offers.map((offer) => ({
    ID: offer.ID,
    name: offer.offer.name,
    investor: offer.offer.investor,
    interest_rate: new BigNumber(offer.offer.interestRate),
    discount_points: new BigNumber(offer.offer.discountPoints),
  }));
}

interface ManagePurchaseOfferDialogProps {
  purchaseID: number;
  purchasePrice: number;
  onConfirm(id: number): unknown;
  offers?: PurchaseOffer[];
  fees: PurchaseFee[];
}

export const ManagePurchaseOfferDialog = (
  props: ManagePurchaseOfferDialogProps,
) => {
  const { offers } = props;
  const offer = offers?.[0];
  const dialog = useDialog();

  const price = useMemo(
    () => toBigNumber(props.purchasePrice || 0),
    [props.purchasePrice],
  );

  const compensationModes = useMemo(
    () => ({
      flat: {
        dp: 2,
        label: "$",
        transformer: Transformers.Base(),
        tooltip: "Flat amount",
      },
      percentage: {
        dp: 4,
        label: "%",
        transformer: Transformers.PercentageOf(price),
        tooltip: "Percentage of the purchase price",
      },
    }),
    [price],
  );

  const { handleSubmit, formState, control } = useForm({
    resolver: zodResolver(ManageOfferBodySchema),
    defaultValues: {
      offers: getOfferDefault(offers),
      mortgage_insurance: new BigNumber(offer?.mortgageInsurance ?? 0),
      years: offer?.offer.term ? offer?.offer.term / 12 : 30,
      amount_due: new BigNumber(offer?.offer.amountDue ?? 0),
      compensation: offer?.offer.compensation ?? null,
      fees: props.fees.map((fee) => ({ name: fee.name, amount: fee.amount })),
    },
  });
  const queryClient = useQueryClient();

  const onSubmit = handleSubmit(async (values) => {
    await Api.PurchaseOffersManage(props.purchaseID, values);

    await queryClient.refetchQueries({
      queryKey: QueryKeys.Purchase(props.purchaseID),
    });

    dialog.close();
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "offers",
  });

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Manage offers</Dialog.Header>
      <Dialog.Body>
        <h3 className="mb-3">Offers</h3>
        {fields.map((_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: No other way
          <div className={styles.offerItem} key={index}>
            <div className={styles.offerItemFields}>
              <TextField
                control={control}
                required
                name={`offers.${index}.name`}
                label="Name"
              />
              <TextField
                control={control}
                name={`offers.${index}.investor`}
                placeholder="Investor"
                label="Investor"
              />
              <NumberInput
                label="Interest rate"
                placeholder="5.00"
                required
                dp={4}
                control={control}
                name={`offers.${index}.interest_rate`}
              />
              <NumberInput
                label="Discount points (%)"
                placeholder="1.00"
                required
                dp={4}
                control={control}
                name={`offers.${index}.discount_points`}
              />
            </div>
            <ButtonIcon
              icon={TbTrash}
              className={styles.offerItemDelete}
              onClick={() => remove(index)}
            >
              Delete
            </ButtonIcon>
          </div>
        ))}
        <Button
          icon={TbPlus}
          onClick={() =>
            append({
              ID: null,
              name: "",
              investor: "",
              discount_points: "",
              interest_rate: "",
            })
          }
        >
          Add new offer
        </Button>
      </Dialog.Body>
      <hr />
      <Dialog.Body>
        <NumberInput
          label="Years"
          placeholder="Years"
          dp={0}
          required
          control={control}
          name="years"
        />
        <NumberInput
          label="Mortgage insurance (%)"
          placeholder="Mortgage insurance"
          dp={3}
          required
          control={control}
          name="mortgage_insurance"
        />
        <FormGroup
          label="Compensation"
          htmlFor="compensation"
          required
          error={formState.errors.compensation?.message}
        >
          <PriceInput
            id="compensation"
            placeholder="1999.00"
            control={control}
            name="compensation"
            modes={compensationModes}
            defaultMode="flat"
            summaryLabel="Amount"
          />
        </FormGroup>
      </Dialog.Body>
      <hr />
      <Dialog.Body>
        <h3 className="mb-3">Fees</h3>
        <FeeListFields control={control} name="fees" />
      </Dialog.Body>
      <Dialog.Footer>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
